"use client"

import { CommonStylesProps } from "@/(admin)/modules/ui-builder/config/lib/commonCSSProps"
import { generateResponsiveCSS } from "@/(admin)/modules/ui-builder/config/lib/helper"
import { Images } from "@/constants/images"
import { DefaultImage } from "@/lib/external-components"
import { toImageSrc } from "@/lib/utils"
import { ComponentConfig } from "@measured/puck"
import Image from "next/image"
import Link from "next/link"
import React from "react"
import { twMerge } from "tailwind-merge"

export interface ContactListProps extends CommonStylesProps {
    contacts: {
        image: string
        title: string
        address: string
        email: string
        phone: string
    }[]
}

interface ContactListContentProps extends Omit<ContactListProps, "styles" | "responsiveType"> {
    id: string
    responsiveCSS: React.ReactNode
}

const ContactListContent: React.FC<ContactListContentProps> = ({
    id,
    className,
    contacts,
    responsiveCSS,
}) => {

    if (!contacts || contacts.length === 0) {
        return null;
    }

    return (
        <>
            {responsiveCSS}
            <div className={twMerge("mx-auto max-w-[33.25rem] sm:max-w-[1320px] max-h-[77.8125rem] sm:max-h-[31.375rem] flex flex-col sm:flex-row w-full px-1", className)} id={id}>

                {contacts.map((contact, index) => {

                    const ImageUrl = contact.image ? toImageSrc(contact.image) : DefaultImage

                    return (
                        <div key={index} className="flex flex-col flex-1 w-full min-w-0 p-3">
                            {/* Image Section */}
                            <div className="w-full h-[214px] relative aspect-[3/2] sm:aspect-[4/2.5] overflow-hidden">
                                <Image
                                    src={ImageUrl}
                                    alt={contact.title || "Contact Image"}
                                    fill
                                    className="object-cover"
                                    sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                                />
                            </div>

                            {/* Title Section */}
                            {
                                contact.title && (
                                    <h3 className="font-bold uppercase text-[rgb(51,51,51)] sm:mt-[2.3125rem] sm:text-[16px] sm:leading-[1.55rem] text-[14px] leading-[21.7px] min-h-[50px] mt-5">
                                        {contact.title}
                                    </h3>
                                )
                            }
                            {/* Phần Address */}
                            {contact.address && (<div className="sm:mt-4 mt-2 flow-root font-normal text-[rgb(51,51,51)] sm:text-[16px] sm:leading-[1.55rem] text-[14px] leading-[21.7px]">
                                <div className="float-left mr-[7px] mt-[3px] sm:mt-[5px]">
                                    <Image
                                        src={Images.iconLocationStore}
                                        alt="location-icon"
                                        width={13}
                                        height={18}
                                        className="block w-[13px] h-[18px]"
                                    />
                                </div>
                                <span >
                                    {contact.address}
                                </span>
                            </div>)}

                            {/* Email */}
                            {contact.email && (<div className="flex items-center mt-[6px] gap-[7px]">
                                <Image src={Images.iconEmail} alt="email-icon" width={16} height={16} className="w-4 h-4 flex-shrink-0" />
                                <Link href={`mailto:${contact.email}`} className="font-normal text-[rgb(55,178,77)] hover:text-[rgb(55,178,77)] sm:text-[16px] sm:leading-[1.55rem] text-[14px] leading-[21.7px]">
                                    {contact.email}
                                </Link>
                            </div>)}

                            {/* Phone */}
                            {contact.phone && (<div className="flex items-center mt-[6px] gap-[7px]">
                                <Image src={Images.iconPhone} alt="phone-icon" width={16} height={16} className="w-4 h-4 flex-shrink-0" />
                                <Link href={`tel:${contact.phone}`} className="font-normal text-[rgba(0, 0, 0, 0)] sm:text-[16px] sm:leading-[1.55rem] text-[14px] leading-[21.7px]">{contact.phone}</Link>
                            </div>)}

                        </div>
                    )
                })}
            </div>

        </>
    )
}

export const RenderConfig: ComponentConfig<ContactListProps> = {
    render: ({ className, styles, responsiveType, contacts }) => {
        const id = `contacts-${Math.random().toString(36).substr(2, 9)}`
        const responsiveCSS = generateResponsiveCSS(id, styles || {}, responsiveType)

        return (
            <ContactListContent
                id={id}
                className={className}
                contacts={contacts}
                responsiveCSS={responsiveCSS}
            />
        )
    },
}



